<template>
    <div>
        <loading v-if="loading"></loading>
        <div v-else-if="showSuccess">
            <template v-if="!isAdminForm">
                <div v-html="formattedCreatePostSuccessText"></div>
                <div v-if="isAdmin">
                    <b-alert variant="info" show>Admins bypass moderation reviews.</b-alert>
                </div>
            </template>
            <template v-else>
                <p class="m-0">Post successfully added.</p>
            </template>

            <div class="d-flex justify-content-between mt-4">
                <b-button variant="outline" @click="cancel(false)">Back</b-button>
                <div>
                    <a class="btn btn-outline mr-2" v-if="isModal" @click="$emit('close-modal')">{{ $t('common.close') }}</a>
                    <a class="btn btn-primary" :href="post.url" :target="submissionLinkTarget">{{ $t('post.view_post') }}</a>
                </div>
            </div>
        </div>
        <template v-else>
            <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.stop.prevent="passes(submitPost)">
                    <template v-if="isAdminForm">

                        <b-alert show v-if="backendErrors.length" variant="danger">
                            <ul class="list-unstyled m-0">
                                <li v-for="error in backendErrors" :key="error">{{ error }}</li>
                            </ul>
                        </b-alert>

                        <b-row>
                            <b-col md="8">
                                <b-card class="h-100">
                                    <validation-provider name="title" rules="min:2|max:180|required" v-slot="{ errors }">
                                        <b-form-group label="Title" label-for="post-title" label-class="font-weight-bold">
                                            <b-form-input id="post-title" v-model="newPost.title" :placeholder="postTitleLabel" required @click="mayPromptLogin" @input="checkDuplicates"></b-form-input>
                                            <span class="validation">{{ errors[0] }}</span>
                                        </b-form-group>
                                    </validation-provider>

                                    <div v-if="duplicates.length">
                                        <p class="mt-2 mb-1 font-weight-bold small">{{ $t('post.duplicates_header') }}</p>
                                        <div class="list-group mb-2">
                                            <div
                                                v-for="duplicate in duplicates"
                                                :key="duplicate.id"
                                                class="list-group-item list-group-item-action"
                                            >
                                                <div
                                                    @click="toggleItem(duplicate.id)"
                                                    class="d-flex justify-content-between align-items-center cursor-pointer small"
                                                >
                                                    {{ duplicate.title }}
                                                    <span class="badge badge-primary badge-pill">
                                            {{ isExpanded(duplicate.id) ? $t('common.close') : $t('common.view') }}
                                          </span>
                                                </div>
                                                <b-collapse :id="`collapse-${duplicate.id}`" class="mt-2">
                                                    <hr>
                                                    <p class="font-weight-bold">{{ duplicate.title }}</p>
                                                    <p>{{ duplicate.description }}</p>

                                                    <a :href="duplicate.url"
                                                       target="_blank"
                                                       class="btn btn-primary"
                                                    >
                                                        {{ $t('common.view') }}
                                                    </a>
                                                </b-collapse>
                                            </div>
                                        </div>
                                    </div>

                                    <b-form-group label="Description" label-for="post-description" label-class="font-weight-bold">
                                        <vue-easymde v-model="newPost.description" ref="markdownEditor" :configs="markdownConfigs" />
                                    </b-form-group>

                                    <b-form-group v-if="showImageUpload" label="Card Image" label-for="post-image" label-class="font-weight-bold">
                                        <b-form-file
                                            id="post-image"
                                            v-model="newPost.image"
                                            accept="image/*"
                                            :placeholder="$t('common.attachment_placeholder_choose')"
                                            :drop-placeholder="$t('common.attachment_placeholder_drop')"
                                        ></b-form-file>
                                    </b-form-group>

                                </b-card>
                            </b-col>
                            <b-col md="4">
                                <b-card class="h-100">

                                    <b-form-group label="Author" label-for="post-user" label-class="font-weight-bold">
                                        <div class="position-relative">
                                            <b-form-input
                                                id="post-user"
                                                v-model="userQuery"
                                                @input="onUserInput"
                                                placeholder="Search or empty for you"
                                            ></b-form-input>
                                            <b-spinner v-if="userLoading" small class="position-absolute" style="top: 10px; right: 10px;"></b-spinner>
                                        </div>
                                        <ul v-if="showUserList" class="list-group mt-2">
                                            <li
                                                v-for="user in users"
                                                :key="user.id"
                                                class="list-group-item list-group-item-action"
                                                @click="selectUser(user)"
                                            >
                                                {{ user.name }}
                                            </li>
                                            <li
                                                class="list-group-item list-group-item-action text-primary"
                                                @click="createNewUser"
                                            >
                                                Create New User
                                            </li>
                                        </ul>
                                    </b-form-group>

                                    <template v-if="tabType !== 'knowledgebase'">
                                        <b-form-group label="Status" label-for="post-status" label-class="font-weight-bold">
                                            <b-form-select id="post-status" v-model="newPost.status" :options="statusOptions"></b-form-select>
                                            <p class="small text-muted m-0">Change the status to move posts across tabs.</p>

                                        </b-form-group>

                                        <b-form-group v-if="showSectionSelect" label="Section" label-for="post-section" label-class="font-weight-bold">
                                            <b-form-select id="post-section" v-model="newPost.section" :options="sectionOptions"></b-form-select>
                                        </b-form-group>
                                    </template>

                                    <b-button v-b-toggle.organize-fields-collapse variant="outline" class="mb-2 btn-block text-left">
                                        Organize
                                    </b-button>

                                    <b-collapse id="organize-fields-collapse" class="mt-2">
                                        <validation-provider v-if="tabType !== 'knowledgebase'" name="category" :rules="categoryRules" v-slot="{ errors }">
                                            <b-form-group label="Category" label-for="post-category" label-class="font-weight-bold">
                                                <b-form-select
                                                    id="post-category"
                                                    v-model="newPost.category"
                                                    :options="categoriesOptions"
                                                    placeholder="Select a category...">
                                                    <template v-slot:first>
                                                        <b-form-select-option value="null">-- Select Category --</b-form-select-option>
                                                    </template>
                                                </b-form-select>
                                                <span class="validation">{{ errors[0] }}</span>
                                            </b-form-group>
                                        </validation-provider>

                                        <!-- Platform Field -->
                                        <b-form-group label="Platform" label-for="post-platform" label-class="font-weight-bold">
                                            <b-form-select id="post-platform" v-model="newPost.platform" :options="platformOptions" placeholder="Select a platform"></b-form-select>
                                        </b-form-group>

                                        <!-- Version Field -->
                                        <validation-provider name="version" rules="max:190" v-slot="{ errors }">
                                            <b-form-group label="Version" label-for="post-version" label-class="font-weight-bold">
                                                <b-form-input id="post-version" v-model="newPost.version" placeholder="Enter version (e.g., 1.0.0)"></b-form-input>
                                                <span class="validation">{{ errors[0] }}</span>
                                            </b-form-group>
                                        </validation-provider>

                                        <!-- Release Field -->
                                        <validation-provider name="release" rules="max:190" v-slot="{ errors }">
                                            <b-form-group label="Release" label-for="post-release" label-class="font-weight-bold">
                                                <b-form-input id="post-release" v-model="newPost.release" placeholder="Enter release information (e.g., Q1 2024)"></b-form-input>
                                                <span class="validation">{{ errors[0] }}</span>
                                            </b-form-group>
                                        </validation-provider>

                                        <b-form-group label="Tags" label-for="post-tags" label-class="font-weight-bold">
                                            <b-form-tags
                                                input-id="post-tags"
                                                v-model="newPost.tags"
                                                :input-attrs="{ autocomplete: 'off' }"
                                                :tag-validator="validateTag"
                                                :limit="20"
                                                tag-variant="dark"
                                                separator=","
                                                placeholder="Type to add tags..."
                                                no-add-on-enter
                                                add-button-variant="primary"
                                                class="mb-2"
                                            ></b-form-tags>
                                            <div v-if="validationMessage" :class="{'text-danger': !validationState, 'text-success': validationState}">
                                                {{ validationMessage }}
                                            </div>
                                        </b-form-group>
                                    </b-collapse>

                                    <b-button v-b-toggle.prioritize-fields-collapse variant="outline" class="mb-2 btn-block text-left">
                                        Prioritize
                                    </b-button>

                                    <b-collapse id="prioritize-fields-collapse" class="mt-2">
                                        <p>These settings will be used for prioritizing the post later.</p>
                                        <!-- Prioritization Impact -->
                                        <validation-provider name="prioritizationImpact" rules="integer|between:0,100" v-slot="{ errors }">
                                            <b-form-group v-if="tabType !== 'knowledgebase'" label="Prioritization Impact" label-for="post-prioritization-impact" label-class="font-weight-bold">
                                                <b-form-input id="post-prioritization-impact" v-model="newPost.prioritizationImpact" type="number" min="0" max="100"></b-form-input>
                                                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                                <p class="small text-muted m-0">Value between 0-100.</p>
                                            </b-form-group>
                                        </validation-provider>

                                        <!-- Prioritization Confidence -->
                                        <validation-provider name="prioritizationConfidence" rules="integer|between:0,100" v-slot="{ errors }">
                                            <b-form-group v-if="tabType !== 'knowledgebase'" label="Prioritization Confidence" label-for="post-prioritization-confidence" label-class="font-weight-bold">
                                                <b-form-input id="post-prioritization-confidence" v-model="newPost.prioritizationConfidence" type="number" min="0" max="100"></b-form-input>
                                                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                                <p class="small text-muted m-0">Value between 0-100.</p>
                                            </b-form-group>
                                        </validation-provider>

                                        <!-- Prioritization Ease -->
                                        <validation-provider name="prioritizationEase" rules="integer|between:0,100" v-slot="{ errors }">
                                            <b-form-group v-if="tabType !== 'knowledgebase'" label="Prioritization Ease" label-for="post-prioritization-ease" label-class="font-weight-bold">
                                                <b-form-input id="post-prioritization-ease" v-model="newPost.prioritizationEase" type="number" min="0" max="100"></b-form-input>
                                                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                                <p class="small text-muted m-0">Value between 0-100.</p>
                                            </b-form-group>
                                        </validation-provider>

                                        <!-- Prioritization Reach -->
                                        <validation-provider name="prioritizationReach" rules="integer|between:0,100" v-slot="{ errors }">
                                            <b-form-group v-if="tabType !== 'knowledgebase'" label="Prioritization Reach" label-for="post-prioritization-reach" label-class="font-weight-bold">
                                                <b-form-input id="post-prioritization-reach" v-model="newPost.prioritizationReach" type="number" min="0" max="100"></b-form-input>
                                                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                                <p class="small text-muted m-0">Value between 0-100.</p>
                                            </b-form-group>
                                        </validation-provider>
                                    </b-collapse>



                                    <b-button v-b-toggle.more-fields-collapse variant="outline" class="mb-2 btn-block text-left">
                                        More Fields
                                    </b-button>

                                    <b-collapse id="more-fields-collapse" class="mt-2">
                                        <b-form-group v-if="isAdminForm" label="Created At" label-for="post-created-at" label-class="font-weight-bold">
                                            <b-form-datepicker id="post-created-at" v-model="newPost.createdAt"></b-form-datepicker>
                                        </b-form-group>

                                        <b-form-group label="Estimated Date" v-if="tabType !== 'knowledgebase'" label-for="post-estimated-date" label-class="font-weight-bold">
                                            <b-form-datepicker id="post-estimated-date" v-model="newPost.estimatedDate" class="mb-2"></b-form-datepicker>
                                        </b-form-group>

                                        <b-form-group v-if="tabType !== 'knowledgebase'" label="Number of Votes" label-for="post-manual-bonus-votes" label-class="font-weight-bold">
                                            <b-form-input id="post-manual-bonus-votes" v-model="newPost.manualBonusVotes" type="number"></b-form-input>
                                        </b-form-group>

                                        <b-form-group label="Attachment" label-for="post-attachments" label-class="font-weight-bold">
                                            <!--                                        multiple-->
                                            <b-form-file
                                                id="post-attachment"
                                                v-model="newPost.attachment"
                                                @input="handleAttachmentUpload"
                                                :placeholder="$t('common.attachment_placeholder_choose')"
                                                :drop-placeholder="$t('common.attachment_placeholder_drop')"
                                            ></b-form-file>
                                        </b-form-group>

                                        <b-form-group label="Publish Status" label-for="post-is-published" label-class="font-weight-bold">
                                            <b-form-checkbox id="post-is-published" v-model="newPost.isPublished" switch>
                                                {{ newPost.isPublished ? 'Published' : 'Draft' }}
                                            </b-form-checkbox>
                                        </b-form-group>

                                        <b-form-group label="Allow Comments" label-class="font-weight-bold">
                                            <b-form-checkbox switch v-model="newPost.allowComments">Allow comments on this post</b-form-checkbox>
                                        </b-form-group>
                                    </b-collapse>

                                </b-card>
                            </b-col>
                        </b-row>
                    </template>

                    <template v-if="!isAdminForm">
                        <div v-if="isAdmin" class="float-right">
                            <a class="btn btn-no-outline ml-2" href="/admin/settings#posting" v-b-tooltip title="Adjust settings for posting.">
                                <i class="bi bi-gear text-muted" aria-hidden="true"></i>
                            </a>
                        </div>
                        <div v-html="formattedCreatePostIntroText"></div>

                        <validation-provider name="title" rules="min:2|max:180|required" v-slot="{ errors }">
                            <b-form-group label="" label-for="post-title" label-class="font-weight-bold">
                                <b-form-input id="post-title" v-model="newPost.title" :placeholder="postTitleLabel" required @click="mayPromptLogin" @input="checkDuplicates"></b-form-input>
                                <span class="validation">{{ errors[0] }}</span>
                            </b-form-group>
                        </validation-provider>

                        <div v-if="duplicates.length">
                            <p class="mt-2 mb-1 font-weight-bold small">{{ $t('post.duplicates_header') }}</p>
                            <div class="list-group mb-2">
                                <div
                                    v-for="duplicate in duplicates"
                                    :key="duplicate.id"
                                    class="list-group-item list-group-item-action"
                                >
                                    <div
                                        @click="toggleItem(duplicate.id)"
                                        class="d-flex justify-content-between align-items-center cursor-pointer small"
                                    >
                                        {{ duplicate.title }}
                                        <span class="badge badge-primary badge-pill">
                                            {{ isExpanded(duplicate.id) ? $t('common.close') : $t('common.view') }}
                                          </span>
                                    </div>
                                    <b-collapse :id="`collapse-${duplicate.id}`" class="mt-2">
                                        <hr>
                                        <p class="font-weight-bold">{{ duplicate.title }}</p>
                                        <p>{{ duplicate.description }}</p>

                                        <a :href="duplicate.url"
                                        target="_blank"
                                        class="btn btn-primary"
                                        >
                                        {{ $t('common.view') }}
                                        </a>
                                    </b-collapse>
                                </div>
                            </div>
                        </div>

                        <b-form-group label="" label-for="post-description" label-class="font-weight-bold">
                            <vue-easymde v-model="newPost.description" ref="markdownEditor" :configs="markdownConfigs" />
                        </b-form-group>

                        <template v-if="displayCategoryBox">
                            <validation-provider name="category" :rules="categoryRules" v-slot="{ errors }">
                                <b-form-group label="" label-for="post-category" label-class="font-weight-bold">
                                    <b-form-select
                                        id="post-category"
                                        v-model="newPost.category"
                                        :options="categoriesOptions"
                                        placeholder="Select a category...">
                                        <template v-slot:first>
                                            <b-form-select-option value="null" v-if="allowUsersToSelectCategory">-- {{$t('post.label_category')}} --</b-form-select-option>
                                            <b-form-select-option value="null" v-else>-- {{$t('post.label_category')}} (admin only) --</b-form-select-option>
                                        </template>
                                    </b-form-select>
                                    <span class="validation">{{ errors[0] }}</span>
                                </b-form-group>
                            </validation-provider>
                        </template>

                    </template>

                    <template v-if="displayAttachment">
                        <validation-provider name="image" rules="" v-slot="{ errors }">
                            <b-form-group :label="$t('post.label_attachment')" label-for="post-attachment" label-class="font-weight-bold">
                                <b-form-file
                                    id="post-attachment"
                                    v-model="newPost.attachment"
                                    @input="handleAttachmentUpload"
                                    :placeholder="$t('common.attachment_placeholder_choose')"
                                    :drop-placeholder="$t('common.attachment_placeholder_drop')"
                                ></b-form-file>
                                <span class="validation">{{ errors[0] }}</span>
                            </b-form-group>
                        </validation-provider>
                    </template>

                    <div class="my-2 d-flex justify-content-end">
                        <b-button variant="link" class="mr-2" @click="displayAttachment=!displayAttachment" v-if="!isAdminForm"><b-icon icon="paperclip" class="text-body" rotate="45"></b-icon></b-button>
                        <b-button variant="outline" class="mr-2" @click="cancel()">{{$t('common.cancel')}}</b-button>
                        <b-button type="submit" variant="primary">{{$t('post.create_button')}}</b-button>
                    </div>
                </b-form>
            </validation-observer>
        </template>
    </div>
</template>

<script>
import VueEasymde from "vue-easymde";
import NotificationPop from "../mixins/NotificationPop";
import Loading from "./Loading";
import { mapState } from "vuex";

export default {
    components: {
        VueEasymde,
        Loading
    },
    mixins: [NotificationPop],
    props: {
        // Presets
        status: Number,
        section: Number,
        category: Number,

        categories: {
            type: Array,
            required: false,
            default: () => []  // Ensuring it's initialized as an array
        },
        isAdmin: {
            type: Boolean,
            required: true
        },
        isAdminForm: {
            type: Boolean,
            default: false
        },
        statuses: {
            type: Array,
            required: false,
            default: () => []
        },
        sections: {
            type: Array,
            required: false,
            default: () => []
        },
        isModal: {
            type: Boolean,
            default: false
        },
        tabType: {
            type: String,
            default: ''
        },
        submissionLinkTarget: {
            type: String,
            default: '_parent',
            validator: function(value) {
                return ['_blank', '_parent', '_self', '_top'].includes(value)
            }
        },
    },
    data() {
        return {
            loading: true,
            loadingDuplicates: false,
            backendErrors: [],
            duplicates: [],
            expandedItems: {},
            timeoutID: null,
            displayAttachment: false,
            attachmentIds: [],
            post: [],
            showSuccess: false,
            selectedTags: [],
            existingTags: [],
            validationState: null,
            validationMessage: '',
            users: [],
            showUserList: false,
            selectedUser: null,
            userQuery: '',
            userLoading: false,
            newPost: {},
            emptyNewPost: {
                title: '',
                description: '',
                category: null,
                attachment: null,
                status: null,
                section: null,
                estimatedDate: null,
                isPublished: true, // Default to true
                tags: [],
                createdAt: null,
                manualBonusVotes: 0,
                prioritizationImpact: null,
                prioritizationConfidence: null,
                prioritizationEase: null,
                prioritizationReach: null,
                allowComments: true,
                image: null,
                userId: null,
                version: '',
                release: '',
                platform: null,
            },
            markdownConfigs: {
                spellChecker: false,
                promptURLs: true,
                toolbarButtonClassPrefix: 'eme-icon',
                previewImagesInEditor: true,
                minHeight: "100px",
                status: false,
                forceSync: true,
                placeholder: "Any additional details...",
                uploadImage: true,
                // Match this with master.blade.php
                toolbar: [
                    "bold", "italic", "strikethrough", "heading", "code", "quote", "unordered-list", "ordered-list", "link", "upload-image", "horizontal-rule", "preview", "side-by-side", "fullscreen", "table",
                ],
                imageUploadFunction: (file, onSuccess, onError) => {
                    // Validate file type (only images) and size (max 5MB)
                    if (!file.type.startsWith("image/")) {
                        onError("Invalid file type. Please upload an image.");
                        return;
                    }
                    if (file.size > 5 * 1024 * 1024) { // Limit to 5MB
                        onError("File size exceeds the maximum limit of 5MB.");
                        return;
                    }

                    const formData = new FormData();
                    formData.append("image", file);

                    fetch("/http_api/images", {//web route
                        method: "POST",
                        body: formData,
                    })
                        .then((response) => response.json())
                        .then((result) => {
                            if (result.url && result.id) {
                                this.attachmentIds.push(result.id); // Save the uploaded image ID
                                onSuccess(result.url); // Pass the image URL to EasyMDE
                            } else {
                                onError("Invalid server response. Please try again.");
                            }
                        })
                        .catch((error) => {
                            console.error("Error uploading image:", error);
                            onError("Image upload failed. Please try again.");
                        });
                }

            }
        };
    },
    watch: {
        status(newValue) {
            this.newPost.status = newValue;
        },
        category(newValue) {
            this.newPost.category = newValue;
        },
        section(newValue) {
            this.newPost.section = newValue;
        }
    },
    computed: {
        showImageUpload() {
            return this.tabType === 'kanban';
        },
        categoriesOptions() {
            return this.categories.map(category => ({
                text: category.name,
                value: category.id
            }));
        },
        ...mapState(['allowUsersToSelectCategory', 'requireCategory', 'formattedCreatePostIntroText', 'isLoggedIn', 'allowAnonymousToCreatePosts', 'postTitleLabel', 'postDescriptionLabel', 'formattedCreatePostSuccessText', 'enableDuplicateDetection']),
        displayCategoryBox() {
            return (this.allowUsersToSelectCategory || this.isAdmin) && this.categories && this.categories.length;
        },
        categoryRules() {
            return (this.requireCategory && !this.isAdminForm) ? 'required' : '';
        },
        statusOptions() {
            return this.isAdminForm ? this.statuses.map(status => ({
                value: status.id,
                text: `${status.name}${status.tab ? ` (${status.tab.nav_title})` : ''}`,
                sections: status.sections || []
            })) : [];
        },
        sectionOptions() {
            return this.isAdminForm ? this.sections.map(section => ({
                value: section.id,
                text: section.title
            })) : [];
        },
        showSectionSelect() {
            return this.isAdminForm && this.sections.length > 0;
        },
        platformOptions() {
            const platforms = [
                'ios',
                'android',
                'web',
                'windows',
                'macos',
                'linux',
                'playstation-5',
                'xbox-series-xs',
                'nintendo-switch',
                'alexa',
                'google-assistant',
                'siri',
                'chrome-extension',
                'firefox-extension',
                'safari-extension',
                'edge-extension',
                'api',
                'sdk',
            ];
            return platforms.map(platform => ({
                text: platform.charAt(0).toUpperCase() + platform.slice(1).replace(/-/g, ' '),
                value: platform
            }));
        },
    },
    created() {
        this.initializeNewPost();
        this.loading = false;
        this.markdownConfigs.placeholder = this.postDescriptionLabel;
        if(this.isAdminForm) {
            this.fetchTags();
        }
    },
    methods: {
        validateTag(tag) {
            return new Promise((resolve) => {
                if (tag.length < 2) {
                    this.validationState = false;
                    this.validationMessage = 'Tag must be at least 2 characters long';
                    resolve(false);
                } else {
                    this.validationState = true;
                    this.validationMessage = '';
                    resolve(true);
                }
            });
        },
        checkDuplicates() {
            if(!this.enableDuplicateDetection) return false;

            this.loadingDuplicates = true;
            this.duplicates = [];

            if (this.timeoutID) {
                clearTimeout(this.timeoutID);
            }

            this.timeoutID = setTimeout(() => {
                this.fetchDuplicates(this.newPost.title);
            }, 500);
        },
        fetchDuplicates(title) {
            if (title.length < 3 || title.length > 180) {
                console.log('Title is too short or too long to check for duplicates');
                return;
            }

            this.loadingDuplicates = true;
            this.duplicates = [];

            this.$http({
                method: 'get',
                url: '/http_api/search/duplicates',
                params: { title: title },
                headers: { 'Content-Type': 'application/json' }
            })
                .then(response => {
                    if (response.data.count > 0) {
                        this.duplicates = response.data.posts;
                        console.log(`Found ${response.data.count} potential duplicates`);
                    } else {
                        console.log('No duplicates found');
                    }
                })
                .catch(error => {
                    console.error('Error fetching duplicates:', error);

                    let errorMessage = 'Failed to check for duplicates.';

                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        console.error('Server responded with error:', error.response.status, error.response.data);
                        errorMessage += ` Server error: ${error.response.status}`;
                    } else if (error.request) {
                        // The request was made but no response was received
                        console.error('No response received from server');
                        errorMessage += ' No response from server.';
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.error('Error setting up the request:', error.message);
                        errorMessage += ' Request setup failed.';
                    }

                    this.popNotification(errorMessage, 'danger');
                })
                .finally(() => {
                    this.loadingDuplicates = false;
                });
        },
        submitPost() {
            if(this.mayPromptLogin()) return;

            this.backendErrors = [];

            let formData = new FormData();
            formData.append('title', this.newPost.title);
            formData.append('description', this.newPost.description);

            if (this.newPost.version) {
                formData.append('version', this.newPost.version);
            }

            if (this.newPost.release) {
                formData.append('release', this.newPost.release);
            }

            if (this.newPost.platform) {
                formData.append('platform', this.newPost.platform);
            }

            // Nullable does not work well in Safari, so remove fields if needed
            if (this.newPost.status) {
                formData.append('status_id', this.newPost.status);
            }

            // Format the estimated date as YYYY-MM-DD if it exists
            if (this.newPost.estimatedDate) {
                const date = new Date(this.newPost.estimatedDate);
                const formattedDate = date.toISOString().split('T')[0];
                formData.append('estimated_date', formattedDate);
            }

            if (this.newPost.createdAt) {
                const date = new Date(this.newPost.createdAt);
                const formattedDate = date.toISOString().split('T')[0];
                formData.append('created_at', formattedDate);
            }

            if (this.newPost.manualBonusVotes) {
                formData.append('manual_bonus_votes', this.newPost.manualBonusVotes);
            }

            if (this.newPost.prioritizationImpact) {
                formData.append('prioritization_impact', this.newPost.prioritizationImpact);
            }

            if (this.newPost.prioritizationConfidence) {
                formData.append('prioritization_confidence', this.newPost.prioritizationConfidence);
            }

            if (this.newPost.prioritizationEase) {
                formData.append('prioritization_ease', this.newPost.prioritizationEase);
            }

            if (this.newPost.prioritizationReach) {
                formData.append('prioritization_reach', this.newPost.prioritizationReach);
            }

            formData.append('allow_comments', this.newPost.allowComments ? 'true' : 'false');

            if (this.showImageUpload && this.newPost.image) {
                formData.append('image', this.newPost.image);
            }

            if (this.newPost.userId) {
                formData.append('user_id', this.newPost.userId);
            }

            // Convert isPublished to a string 'true' or 'false'
            formData.append('is_published', this.newPost.isPublished ? 'true' : 'false');

            // Only append tags if the array is not empty
            if (Array.isArray(this.newPost.tags) && this.newPost.tags.length > 0) {
                this.newPost.tags.forEach((tag, index) => {
                    formData.append(`tags[${index}]`, tag);
                });
            }

            if (this.newPost.section !== null && this.newPost.section !== undefined) {
                formData.append('section_id', this.newPost.section);
            }

            if (this.newPost.category) {
                formData.append('category_id', this.newPost.category);
            }

            if (this.newPost.attachment) {
                formData.append('attachment', this.newPost.attachment);
            }

            this.loading = true;
            this.$http({
                method: 'post',
                url: '/http_api/posts',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(response => {
                    let post = response.data.data;
                    this.$emit('post-added', post);
                    this.post = post;
                    this.showSuccess = true;
                })
                .catch(error => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        const errors = error.response.data.errors;
                        Object.keys(errors).forEach(key => {
                            this.backendErrors = this.backendErrors.concat(errors[key]);
                        });
                    } else {
                        this.backendErrors.push('Failed to add post due to an unexpected error.');
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchTags() {
            this.$http.get('/http_api/tags')
                .then(response => {
                    if (response.data.data && Array.isArray(response.data.data)) {
                        // Extract the 'tag' property from each object and create a new plain array
                        const tags = response.data.data.map(item => item.tag);
                        // Use spread operator to create a new array, breaking the reactivity chain
                        this.existingTags = [...tags];
                    } else {
                        this.existingTags = [];
                    }
                })
                .catch(error => {
                    console.error('Error fetching tags:', error);
                    this.popNotification('Failed to fetch tags.', 'danger');
                    this.existingTags = [];
                });
        },
        handleAttachmentUpload(file) {
            if (file instanceof File) {
                this.newPost.attachment = file;
                console.log('File uploaded:', file.name);
            } else if (Array.isArray(file) && file.length > 0 && file[0] instanceof File) {
                this.newPost.attachment = file[0];
                console.log('File uploaded:', file[0].name);
            } else {
                console.warn('Unexpected input to handleAttachmentUpload:', file);
                this.newPost.attachment = null;
            }
        },
        mayPromptLogin() {
            if(!this.allowAnonymousToCreatePosts && !this.isLoggedIn) {
                $('.login_required').modal('show');
                return true;
            }
        },
        initializeNewPost() {
            this.newPost = {
                ...JSON.parse(JSON.stringify(this.emptyNewPost)),  // Deep copy of emptyNewPost
                status: this.status,
                category: this.category,
                section: this.section
            };
        },
        cancel(emit = true) {
            this.backendErrors = [];
            this.duplicates = [];
            this.displayAttachment = false;
            this.showSuccess = false;
            this.initializeNewPost();  // Use the new method here
            if(emit) {
                this.$emit('cancel');
            }
        },
        onUserInput() {
            const query = this.userQuery;
            if (query.length < 1) {
                this.showUserList = false;
                return;
            }

            this.userLoading = true; // Start user loading
            this.$http.get("/users/autocomplete", { params: { query: query } })
                .then(response => {
                    this.users = response.data.map(user => ({
                        ...user,
                        key: user.key,
                        name: `${user.name} (${user.email})`
                    }));
                    this.showUserList = true;
                })
                .catch(error => {
                    console.error('Error fetching users:', error);
                    this.popNotification('Failed to fetch users.', 'danger');
                })
                .finally(() => {
                    this.userLoading = false; // End user loading
                });
        },

        selectUser(user) {
            this.selectedUser = user;
            this.newPost.userId = user.key;
            this.userQuery = user.name;
            this.showUserList = false;
        },

        createNewUser() {
            // Open a new window with the specified URL
            window.open('/admin/invite/users', '_blank');
        },
        toggleItem(id) {
            this.$set(this.expandedItems, id, !this.expandedItems[id])
            this.$root.$emit('bv::toggle::collapse', `collapse-${id}`)
        },
        isExpanded(id) {
            return this.expandedItems[id] || false
        }
    }
};
</script>
